import React, { useEffect, useState } from 'react'
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Avatar,
  Tab,
  Button,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Badge,
  Tabs
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon, 
  Refresh as RefreshIcon, 
  PlayArrow as PlayArrowIcon, 
  Pause as PauseIcon, 
  StarBorder as StarBorderIcon, 
  ShowChart as ShowChartIcon, 
  CandlestickChart as CandlestickChartIcon, 
  Timeline as TimelineIcon, 
  AutoGraph as AutoGraphIcon, 
  BarChart as BarChartIcon,
  MoreVert as MoreVertIcon,
  Fullscreen as FullscreenIcon,
  ContentCopy as ContentCopyIcon,
  VerifiedUser as VerifiedUserIcon,
  SmartToy as SmartToyIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  People as PeopleIcon
} from '@mui/icons-material';

// Import existing components
import MarketTabs from '../Markets/MarketTabs';
import ListStock from '../Stock/ListStock';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import CoinSlide from '../BybitTrade/CoinSlide';
import BalanceCard from '../Cards/BalanceCard';
import MobileMenu from './MobileMenu';
import StockSlide from '../Stock/StockSlide';
import SmallChart from '../Traders/SmallChart';

// Redux
import { setcurrentPair, setPairType } from '../Trader/tradingSlice';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import { useJointAccountBalance } from '../../hooks/useJointAccountBalance';

// Professional Trading Dashboard Styled Components
const TradingDashboard = styled(Box)(({ theme }) => ({
  padding: '0 8px 0 8px',
  background: '#0B0E11',
  minHeight: '100vh',
  color: '#FFFFFF',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.up('md')]: {
    padding: '0 12px 0 12px',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
      transform: 'scale(1)'
    },
    '50%': {
      opacity: 0.7,
      transform: 'scale(1.1)'
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1)'
    }
  }
}));

const TradingHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
  padding: '8px 12px',
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '6px',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: '6px 8px',
    marginBottom: '6px'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 50%, #F7931A 100%)',
  }
}));

const CopyTraderCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '4px',
  padding: '8px',
  minWidth: '180px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    minWidth: '150px',
    padding: '6px'
  },
  '&:hover': {
    borderColor: '#7ead49',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(126, 173, 73, 0.2)'
  }
}));

const CopySlider = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  height: '130px',
  overflowY: 'hidden',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    height: '110px',
    gap: '5px'
  },
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    height: '8px',
    zIndex: 2,
    pointerEvents: 'none'
  },
  '&::before': {
    top: 0,
    background: 'linear-gradient(to bottom, #161A1E, transparent)'
  },
  '&::after': {
    bottom: 0,
    background: 'linear-gradient(to top, #161A1E, transparent)'
  }
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  animation: 'verticalScroll 45s linear infinite',
  [theme.breakpoints.down('md')]: {
    gap: '5px',
    animation: 'verticalScroll 35s linear infinite'
  },
  '&:hover': {
    animationPlayState: 'paused'
  },
  '@keyframes verticalScroll': {
    '0%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-100%)' },
    '100%': { transform: 'translateY(0)' }
  }
}));

const PriceTickerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#F7931A',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(247, 147, 26, 0.2)'
  }
}));

const TradingCard = styled(Card)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '100%',
  '& .MuiCardContent-root': {
    padding: '16px',
    '&:last-child': {
      paddingBottom: '16px'
    }
  }
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '400px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column'
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #2B3139',
  background: '#1E2328'
}));

const OrderBookContainer = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '400px',
  overflow: 'hidden'
}));

const OrderBookHeader = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #2B3139',
  background: '#1E2328',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const QuickTradePanel = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  padding: '16px'
}));

const TradeButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #02C076 0%, #05E085 100%)',
  color: '#FFFFFF',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '6px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #05E085 0%, #02C076 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(2, 192, 118, 0.4)'
  },
  '&.sell': {
    background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #FF6B85 0%, #F84960 100%)',
      boxShadow: '0 4px 12px rgba(248, 73, 96, 0.4)'
    }
  }
}));

const PortfolioMetric = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '16px',
  background: '#1E2328',
  borderRadius: '6px',
  border: '1px solid #2B3139',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#7ead49',
    background: '#2B3139'
  }
}));

const LiveChart = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(2, 192, 118, 0.05)',
  position: 'relative',
  '&::after': {
    content: '"⚡"',
    position: 'absolute',
    fontSize: '48px',
    opacity: 0.1
  }
}));

const AssetTabs = styled(Tabs)(({ theme }) => ({
  background: '#1E2328',
  borderRadius: '8px',
  minHeight: '40px',
  '& .MuiTabs-indicator': {
    backgroundColor: '#F7931A',
    height: '3px',
    borderRadius: '2px'
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    minHeight: '40px',
    color: '#B7BDC6',
    '&.Mui-selected': {
      color: '#F7931A'
    }
  }
}));

const AssetTickerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#7ead49',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(126, 173, 73, 0.15)'
  }
}));

const DepositButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
  color: '#000',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  minWidth: 'auto',
  '&:hover': {
    background: 'linear-gradient(135deg, #FFD700 0%, #F7931A 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)'
  },
  [theme.breakpoints.down('md')]: {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const Index = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.user);
  const coinRates = useSelector((state) => state.coins.coinRates);
  const stock = useSelector((state) => state.coins.stock);
  const fundings = useSelector((state) => state.wallet.fundings);
  const tradings = useSelector((state) => state.history.tradings);
  const investments = useSelector((state) => state.history.investments);
  const withdrawals = useSelector((state) => state.history.withdrawals);
  const traders = useSelector((state) => state.traders?.traders) || [];
  const fundingBal = useSelector((state) => state.wallet.fundingBalance); // Real total assets
  const subBal = useSelector((state) => state.wallet.subscriptionBalance); // Invested Capital
  const mainBal = useSelector((state) => state.wallet.withdrawableBalance); // Accumulated Profit
  const refBal = useSelector((state) => state.wallet.referralBalance); // Referral Wallet
  
  // Get joint account balance
  const { data: jointAccountData, isLoading: jointAccountLoading } = useJointAccountBalance();
  const jointAccountBalance = jointAccountData?.totalBalance || 0;
  const jointAccountCount = jointAccountData?.accountCount || 0;
  
  const plans = useSelector((state) => state.plans.plans);
  const categories = useSelector((state) => state.plans.categories);
  
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
  const [orderBookData, setOrderBookData] = useState({ bids: [], asks: [] });
  const [activeTab, setActiveTab] = useState('0');
  const [assetType, setAssetType] = useState('crypto'); // Changed default to 'crypto'

  // Get joint investments from Redux (same as /copy-plans page)
  const jointInvestments = useSelector((state) => state.traders.traders) || [];
  const loadingJointInvestments = false;

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

  // Joint investment utility functions
  const formatDate = (date) => new Date(date).toLocaleDateString();

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return '#02C076';
      case 'medium': return '#FFD700';
      case 'high': return '#F84960';
      default: return '#B7BDC6';
    }
  };



  // Remove mock real-time data updates - keeping only orderbook simulation for trading functionality
  useEffect(() => {
    const interval = setInterval(() => {
      // Only simulate orderbook updates for trading functionality
      const newBids = Array.from({ length: 10 }, (_, i) => ({
        price: (50000 - i * 10 + Math.random() * 5).toFixed(2),
        amount: (Math.random() * 10).toFixed(4),
        total: (Math.random() * 500000).toFixed(2)
      }));
      const newAsks = Array.from({ length: 10 }, (_, i) => ({
        price: (50020 + i * 10 + Math.random() * 5).toFixed(2),
        amount: (Math.random() * 10).toFixed(4),
        total: (Math.random() * 500000).toFixed(2)
      }));
      setOrderBookData({ bids: newBids, asks: newAsks });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const topPairs = [
    { symbol: 'BTC/USDT', price: '97,234.50', change: '+2.45%', volume: '1.2B', isPositive: true },
    { symbol: 'ETH/USDT', price: '3,456.78', change: '+1.23%', volume: '890M', isPositive: true },
    { symbol: 'BNB/USDT', price: '645.32', change: '-0.87%', volume: '456M', isPositive: false },
    { symbol: 'SOL/USDT', price: '234.56', change: '+5.67%', volume: '234M', isPositive: true },
    { symbol: 'ADA/USDT', price: '1.2345', change: '+3.21%', volume: '123M', isPositive: true },
    { symbol: 'DOT/USDT', price: '12.34', change: '-1.45%', volume: '67M', isPositive: false }
  ];

  const recentTrades = [
    { time: '15:23:45', pair: 'BTC/USDT', side: 'BUY', price: '97,234.50', amount: '0.0123' },
    { time: '15:23:44', pair: 'BTC/USDT', side: 'SELL', price: '97,230.25', amount: '0.0456' },
    { time: '15:23:43', pair: 'BTC/USDT', side: 'BUY', price: '97,235.75', amount: '0.0789' },
    { time: '15:23:42', pair: 'ETH/USDT', side: 'BUY', price: '3,456.78', amount: '1.234' },
    { time: '15:23:41', pair: 'ETH/USDT', side: 'SELL', price: '3,454.32', amount: '2.567' }
  ];

  // Get copy traders data - use only real traders from Redux
  const getCopyTraders = () => {
    return traders.map(trader => ({
      id: trader.id,
      name: trader.trader_name || 'Anonymous',
      level: trader.level || 'Verified',
      roi: `${trader.gain || 0}%`,
      followers: trader.copiers || 0,
      duration: trader.duration || '30 Days',
      image: trader.image || `https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face`,
      verified: true
    }));
  };

  const copyTraders = getCopyTraders();

  const formatFollowers = (count) => {
    count = parseInt(count);
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count;
  };

  // Calculate balance for crypto
  const calculateBalance = (coin) => {
    let amount = 0;
    fundings.forEach(fund => {
      if (fund.gateway === coin) {
        amount += parseFloat(fund.amount);
      }
    });
    tradings.forEach(trade => {
      if (trade.pair === coin) {
        amount += parseFloat(trade.profit);
      }
    });
    investments.forEach(investment => {
      if (investment.payment_source === coin && investment.status !== 'declined') {
        amount -= parseFloat(investment.amount_invested);
      }
    });
    withdrawals.forEach(withdrawal => {
      if (withdrawal.source === coin && withdrawal.status !== 'declined') {
        amount -= parseFloat(withdrawal.amount);
      }
    });
    return amount;
  };

  // Calculate 24hr change for crypto
  const calculate24hrChange = (coin) => {
    let rate = 0;
    coinRates.forEach(coinRater => {
      if (coinRater.symbol === coin) {
        rate = parseFloat(coinRater.changePercent24Hr);
      }
    });
    return rate;
  };

  // Handle asset click
  const handleAssetClick = (asset, type) => {
    if (type === 'crypto') {
      dispatch(setPairType('crypto'));
      if (asset.symbol === 'USDT') {
        dispatch(setcurrentPair('BTC'));
      } else {
        dispatch(setcurrentPair(asset.symbol));
      }
    } else {
      dispatch(setPairType('stocks'));
      dispatch(setcurrentPair(asset.short_name));
    }
    
    setTimeout(() => {
      navigate('/trading-page');
    }, 100);
  };

  // Handle copy trader click
  const handleCopyTraderClick = (traderId) => {
    if (typeof traderId === 'string' && traderId.startsWith('mock')) {
      // For mock traders, redirect to copy-plans page
      navigate('/copy-plans');
    } else {
      // For real traders, redirect to their specific page
      navigate(`/view-project/${traderId}`);
    }
  };

  // Get top assets to display
  const getTopAssets = () => {
    if (assetType === 'crypto') {
      return coinRates.slice(0, 10) || [];
    } else {
      return stock.slice(0, 10) || [];
    }
  };

  // Get portfolio assets
  const getPortfolioAssets = () => {
    const assets = [];
    
    if (assetType === 'crypto') {
      coinRates.slice(0, 6).forEach((coin) => {
        const balance = calculateBalance(coin.symbol);
        const change = calculate24hrChange(coin.symbol);
        assets.push({
          symbol: coin.symbol,
          name: coin.name,
          priceUsd: coin.priceUsd,
          amount: `${(balance/parseFloat(coin.priceUsd)).toFixed(6)} ${coin.symbol}`,
          usdValue: formatPrice(balance).replace('$', ''),
          change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
          logo: `https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`,
          balance,
          volumeUsd24Hr: coin.volumeUsd24Hr
        });
      });
    } else {
      stock.slice(0, 6).forEach((stockItem) => {
        const change = parseFloat(stockItem.change_percentage || 0);
        assets.push({
          symbol: stockItem.short_name,
          name: stockItem.name,
          priceUsd: stockItem.priceUsd,
          amount: `0 shares`, // No user stock balance available
          usdValue: formatPrice(0),
          change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
          logo: `https://img.logo.dev/ticker/${stockItem.short_name}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`,
          balance: 0
        });
      });
    }
    
    return assets;
  };

  const topAssets = getTopAssets();
  const portfolioAssets = getPortfolioAssets();

  return (
    <TradingDashboard>
      {/* Enhanced Balance Card Header */}
      <TradingHeader>
        <Grid container spacing={2} alignItems="center">
          {/* Clean Balance Display */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: { xs: '16px', md: '20px' }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#FFFFFF', 
                    fontSize: { xs: '15px', md: '17px' }, 
                    mb: { xs: 0.5, md: 0.75 },
                    textAlign: 'left'
                  }}
                >
                  Total Assets
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#FFFFFF', 
                    fontSize: { xs: '21px', md: '25px' },
                    textAlign: 'left',
                    lineHeight: 1.1
                  }}
                >
                  {formatPrice(fundingBal)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <DepositButton
                  component={RouterLink}
                  to="/receive"
                  startIcon={
                    <Avatar 
                      src="https://res.cloudinary.com/dqi4rtyvh/image/upload/v1738590761/deposit_bvwyrg.png"
                      sx={{ 
                        width: { xs: 21, md: 23 }, 
                        height: { xs: 21, md: 23 },
                        marginLeft: '-4px',
                        marginRight: '4px'
                      }}
                    />
                  }
                  sx={{
                    fontSize: { xs: '15px', md: '17px' },
                    height: { xs: '33px', md: '37px' },
                    padding: { xs: '6px 12px', md: '8px 16px' }
                  }}
                >
                  Deposit
                </DepositButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </TradingHeader>

      {/* Mobile Menu - Moved here after first section */}
      <Box sx={{ mb: 2 }}>
        <MobileMenu />
      </Box>

      {/* Wallet Balance Cards */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              border: '1px solid #2B3139',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              '&:hover': {
                borderColor: '#F7931A',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(247, 147, 26, 0.2)'
              }
            }}>
              <CardContent sx={{ 
                padding: { xs: '12px !important', md: '16px !important' },
                '&:last-child': { paddingBottom: { xs: '12px !important', md: '16px !important' } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: { xs: 1, md: 1.5 }
                }}>
                  <AccountBalanceWalletIcon sx={{ 
                    fontSize: { xs: 24, md: 28 }, 
                    color: '#F7931A' 
                  }} />
                  <Chip 
                    label="Overall Assets" 
                    size="small" 
                    sx={{ 
                      background: 'rgba(247, 147, 26, 0.1)', 
                      color: '#F7931A',
                      fontSize: { xs: '8px', md: '10px' },
                      fontWeight: 600,
                      height: { xs: '20px', md: '24px' }
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700, 
                  fontSize: { xs: '14px', md: '16px' }, 
                  mb: 0.5,
                  lineHeight: 1.2
                }}>
                  {formatPrice(fundingBal)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#B7BDC6', 
                  fontSize: { xs: '10px', md: '11px' },
                  lineHeight: 1.2
                }}>
                  Total Portfolio Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              border: '1px solid #2B3139',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              '&:hover': {
                borderColor: '#02C076',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(2, 192, 118, 0.2)'
              }
            }}>
              <CardContent sx={{ 
                padding: { xs: '12px !important', md: '16px !important' },
                '&:last-child': { paddingBottom: { xs: '12px !important', md: '16px !important' } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: { xs: 1, md: 1.5 }
                }}>
                  <SavingsIcon sx={{ 
                    fontSize: { xs: 24, md: 28 }, 
                    color: '#02C076' 
                  }} />
                  <Chip 
                    label="Invested Capital" 
                    size="small" 
                    sx={{ 
                      background: 'rgba(2, 192, 118, 0.1)', 
                      color: '#02C076',
                      fontSize: { xs: '8px', md: '10px' },
                      fontWeight: 600,
                      height: { xs: '20px', md: '24px' }
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700, 
                  fontSize: { xs: '14px', md: '16px' }, 
                  mb: 0.5,
                  lineHeight: 1.2
                }}>
                  {formatPrice(subBal)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#B7BDC6', 
                  fontSize: { xs: '10px', md: '11px' },
                  lineHeight: 1.2
                }}>
                  Active Investments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              border: '1px solid #2B3139',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              '&:hover': {
                borderColor: '#FFD700',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(255, 215, 0, 0.2)'
              }
            }}>
              <CardContent sx={{ 
                padding: { xs: '12px !important', md: '16px !important' },
                '&:last-child': { paddingBottom: { xs: '12px !important', md: '16px !important' } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: { xs: 1, md: 1.5 }
                }}>
                  <TrendingUpIcon sx={{ 
                    fontSize: { xs: 24, md: 28 }, 
                    color: '#FFD700' 
                  }} />
                  <Chip 
                    label="Accumulated Profit" 
                    size="small" 
                    sx={{ 
                      background: 'rgba(255, 215, 0, 0.1)', 
                      color: '#FFD700',
                      fontSize: { xs: '8px', md: '10px' },
                      fontWeight: 600,
                      height: { xs: '20px', md: '24px' }
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700, 
                  fontSize: { xs: '14px', md: '16px' }, 
                  mb: 0.5,
                  lineHeight: 1.2
                }}>
                  {formatPrice(mainBal)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#B7BDC6', 
                  fontSize: { xs: '10px', md: '11px' },
                  lineHeight: 1.2
                }}>
                  Total Earnings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              border: '1px solid #2B3139',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              '&:hover': {
                borderColor: '#9C27B0',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(156, 39, 176, 0.2)'
              }
            }}>
              <CardContent sx={{ 
                padding: { xs: '12px !important', md: '16px !important' },
                '&:last-child': { paddingBottom: { xs: '12px !important', md: '16px !important' } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: { xs: 1, md: 1.5 }
                }}>
                  <StarIcon sx={{ 
                    fontSize: { xs: 24, md: 28 }, 
                    color: '#9C27B0' 
                  }} />
                  <Chip 
                    label="Referral Wallet" 
                    size="small" 
                    sx={{ 
                      background: 'rgba(156, 39, 176, 0.1)', 
                      color: '#9C27B0',
                      fontSize: { xs: '8px', md: '10px' },
                      fontWeight: 600,
                      height: { xs: '20px', md: '24px' }
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700, 
                  fontSize: { xs: '14px', md: '16px' }, 
                  mb: 0.5,
                  lineHeight: 1.2
                }}>
                  {formatPrice(refBal)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#B7BDC6', 
                  fontSize: { xs: '10px', md: '11px' },
                  lineHeight: 1.2
                }}>
                  Referral Earnings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Joint Account Balance Card */}
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
              border: '1px solid #2B3139',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              height: '100%',
              '&:hover': {
                borderColor: '#3B82F6',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.2)'
              }
            }}>
              <CardContent sx={{ 
                padding: { xs: '12px !important', md: '16px !important' },
                '&:last-child': { paddingBottom: { xs: '12px !important', md: '16px !important' } }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: { xs: 1, md: 1.5 }
                }}>
                  <AccountBalanceIcon sx={{ 
                    fontSize: { xs: 24, md: 28 }, 
                    color: '#3B82F6' 
                  }} />
                  <Chip 
                    label={`${jointAccountCount} Account${jointAccountCount !== 1 ? 's' : ''}`}
                    size="small" 
                    sx={{ 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      color: '#3B82F6',
                      fontSize: { xs: '8px', md: '10px' },
                      fontWeight: 600,
                      height: { xs: '20px', md: '24px' }
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700, 
                  fontSize: { xs: '14px', md: '16px' }, 
                  mb: 0.5,
                  lineHeight: 1.2
                }}>
                  {jointAccountLoading ? '...' : formatPrice(jointAccountBalance)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#B7BDC6', 
                  fontSize: { xs: '10px', md: '11px' },
                  lineHeight: 1.2
                }}>
                  Joint Account Balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* AI Mining Plans Section - Moved before Collaborative Mining Pools */}
      <Box sx={{ mb: 3 }}>
        <TradingCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '18px' }}>
                AI Mining Plans
              </Typography>
              <Button
                component={RouterLink}
                to="/package/AI Mining Plans"
                size="small"
                sx={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  background: 'rgba(126, 173, 73, 0.1)',
                  color: '#7ead49',
                  borderRadius: '6px',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(126, 173, 73, 0.2)'
                  }
                }}
              >
                View All
              </Button>
            </Box>
            
            {plans && plans.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {plans
                  .filter(plan => {
                    // Handle different category data structures
                    let planCategoryName = '';
                    
                    if (typeof plan.category === 'string') {
                      planCategoryName = plan.category.toLowerCase().trim();
                    } else if (plan.category && typeof plan.category === 'object') {
                      planCategoryName = plan.category.name?.toLowerCase().trim() || '';
                    } else if (plan.category_id) {
                      // Find category name by ID
                      const category = categories.find(cat => cat.id === plan.category_id);
                      planCategoryName = category?.name?.toLowerCase().trim() || '';
                    }
                    
                    const targetCategory = 'ai mining plans';
                    return planCategoryName === targetCategory;
                  })
                  .map((plan, index) => (
                    <Box
                      key={plan.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        background: '#1E2328',
                        borderRadius: '8px',
                        border: '1px solid #2B3139',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#7ead49',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(126, 173, 73, 0.2)'
                        }
                      }}
                      onClick={() => {
                        // Handle different category data structures for navigation
                        let categoryName = '';
                        
                        if (typeof plan.category === 'string') {
                          categoryName = plan.category;
                        } else if (plan.category && typeof plan.category === 'object') {
                          categoryName = plan.category.name || '';
                        } else if (plan.category_id) {
                          // Find category name by ID
                          const category = categories.find(cat => cat.id === plan.category_id);
                          categoryName = category?.name || '';
                        }
                        
                        navigate(`/package/${categoryName}`);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Box
                          sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, rgba(126, 173, 73, 0.2) 0%, rgba(126, 173, 73, 0.1) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(126, 173, 73, 0.3)'
                          }}
                        >
                          <SmartToyIcon sx={{ color: '#7ead49', fontSize: '20px' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '14px' }}>
                            {plan.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '11px' }}>
                            {formatPrice(plan.min_amount)} - {parseInt(plan.amount_invested) >= 1000000 ? 'UNLIMITED' : formatPrice(plan.amount_invested)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: '60px', height: '40px' }}>
                          <SmallChart seed={plan.id} targetPercentage={parseFloat(plan.total_earning)} />
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ color: '#02C076', fontWeight: 700, fontSize: '14px' }}>
                            {parseFloat(plan.total_earning).toFixed(2)}%
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                            ROI
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', padding: '32px 16px' }}>
                <SmartToyIcon sx={{ color: '#6C7293', fontSize: '48px', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                  No AI Mining Plans Available
                </Typography>
                <Typography variant="caption" sx={{ color: '#6C7293', fontSize: '11px' }}>
                  Check back later for new mining plans
                </Typography>
              </Box>
            )}
          </CardContent>
        </TradingCard>
      </Box>

      {/* Collaborative Mining Pools Section */}
      <Box sx={{ mb: 3 }}>
        <TradingCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '18px' }}>
                Collaborative Mining Pools
              </Typography>
              <Button
                component={RouterLink}
                to="/copy-plans"
                size="small"
                sx={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  background: 'rgba(126, 173, 73, 0.1)',
                  color: '#7ead49',
                  borderRadius: '6px',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(126, 173, 73, 0.2)'
                  }
                }}
              >
                View All
              </Button>
            </Box>
            
            {loadingJointInvestments ? (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                <Typography sx={{ color: '#B7BDC6' }}>Loading joint investments...</Typography>
              </Box>
            ) : jointInvestments && jointInvestments.length > 0 ? (
              <Grid container spacing={2}>
                {jointInvestments.slice(0, 3).map((trader) => (
                  <Grid key={trader.id} item xs={12} md={4}>
                    <Card sx={{
                      marginX: '5px',
                      borderRadius: '12px',
                      height: '100%',
                      paddingX: '10px',
                      background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                      border: '1px solid #2B3139',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#7ead49',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(126, 173, 73, 0.2)'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        paddingY: '12px', 
                        paddingX: '5px'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#d7251e',
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                            border: '2px solid #7ead49',
                            boxShadow: '0 4px 12px rgba(126, 173, 73, 0.3)'
                            }} 
                            src={trader.image}
                            variant="rounded"
                          >
                            <GroupIcon sx={{ color: '#FFFFFF' }} />
                          </Avatar>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'flex-start',
                            marginLeft: '12px',
                            flex: 1
                          }}>
                            <Typography sx={{
                              fontSize: 14, 
                              fontWeight: 600,
                              color: '#FFFFFF',
                              textTransform: 'capitalize',
                              lineHeight: 1.2
                            }} 
                            variant="subtitle2" 
                            component="div">
                              {trader.trader_name}
                            </Typography>

                            <Typography sx={{
                              fontSize: 11, 
                              color: '#B7BDC6',
                              textTransform: 'capitalize',
                              marginTop: '2px'
                            }} 
                            variant="caption" 
                            component="div">
                              {trader.level}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: '2px' }}>
                              <Chip
                                label={trader.risk_score > 4 ? 'High Risk' : trader.risk_score > 2 ? 'Medium Risk' : 'Low Risk'}
                                size="small"
                                sx={{
                                  background: trader.risk_score > 4 ? 'rgba(248, 73, 96, 0.2)' : trader.risk_score > 2 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(2, 192, 118, 0.2)',
                                  color: trader.risk_score > 4 ? '#F84960' : trader.risk_score > 2 ? '#FFD700' : '#02C076',
                                  fontSize: '9px',
                                  height: '18px',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Button 
                            color='primary' 
                            variant='contained' 
                            size='small'
                            onClick={() => navigate(`/view-project/${trader.id}`)}
                            sx={{
                              background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
                              color: '#000',
                              fontWeight: 600,
                              fontSize: '11px',
                              minWidth: '60px',
                              height: '28px',
                              '&:hover': {
                                background: 'linear-gradient(90deg, #E8841A 0%, #E6C200 100%)'
                              }
                            }}
                          >
                            Invest
                          </Button>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ borderColor: '#2B3139' }} />

                      <CardContent sx={{ paddingTop: '12px !important', paddingBottom: '16px !important' }}>
                        {/* Progress bar */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '11px' }}>
                              Progress
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '11px' }}>
                              {trader.gain}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(parseFloat(trader.gain) || 0, 100)}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: '#2B3139',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
                                borderRadius: 2
                              }
                            }}
                          />
                        </Box>

                        {/* Key metrics in a row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                              ROI {trader.duration}
                            </Typography>
                            <Typography sx={{ 
                              color: '#02C076', 
                              fontWeight: 600, 
                              fontSize: 12,
                              lineHeight: 1.2
                            }} 
                            variant="subtitle2" 
                            component="div">
                              {trader.gain}%
                            </Typography>
                          </Box>

                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                              Investors
                            </Typography>
                            <Typography sx={{ 
                              color: '#FFFFFF', 
                              fontWeight: 600, 
                              fontSize: 12,
                              lineHeight: 1.2
                            }} 
                            variant="subtitle2" 
                            component="div">
                              {trader.copiers || 0}
                            </Typography>
                          </Box>

                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                              Min Amount
                            </Typography>
                            <Typography sx={{ 
                              color: '#FFFFFF', 
                              fontWeight: 600, 
                              fontSize: 12,
                              lineHeight: 1.2
                            }} 
                            variant="subtitle2" 
                            component="div">
                              {formatPrice(trader.min_amount || 100)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Additional info row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MoneyIcon sx={{ fontSize: 12, color: '#B7BDC6' }} />
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                              {trader.commission || 2}% Fee
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon sx={{ fontSize: 12, color: '#B7BDC6' }} />
                            <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                              {trader.duration}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                                <GroupIcon sx={{ fontSize: 48, color: '#7ead49', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
                  Pool Resources, Share Profits
                </Typography>
                <Typography sx={{ color: '#B7BDC6', mb: 2 }}>
                  Join collaborative mining pools to maximize your mining profits
                </Typography>
                <Button
                  component={RouterLink}
                  to="/copy-plans"
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 100%)',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(90deg, #E8841A 0%, #E6C200 100%)'
                    }
                  }}
                >
                  Explore Mining Pools
                </Button>
              </Box>
            )}
          </CardContent>
        </TradingCard>
      </Box>

      {/* Asset Type Tabs - COMMENTED OUT */}
      {/* 
      <Box sx={{ mb: 3 }}>
        <AssetTabs
          value={assetType}
          onChange={(e, newValue) => setAssetType(newValue)}
          indicatorColor="primary"
        >
          <Tab label="Stocks" value="stocks" />
          <Tab label="Cryptocurrency" value="crypto" />
        </AssetTabs>
      </Box>

      {/* Asset Ticker - COMMENTED OUT */}
      {/* 
      <Box sx={{ mb: 3, overflow: 'hidden' }}>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
          {topAssets.map((asset, index) => (
            <AssetTickerCard 
              key={index} 
              sx={{ minWidth: '180px' }}
              onClick={() => handleAssetClick(asset, assetType)}
            >
              <CardContent sx={{ padding: '10px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      src={assetType === 'crypto' 
                        ? `https://assets.coincap.io/assets/icons/${asset.symbol?.toLowerCase()}@2x.png`
                        : `https://img.logo.dev/ticker/${asset.short_name}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`
                      }
                      sx={{ width: 20, height: 20 }}
                    >
                      {(asset.symbol || asset.short_name)?.[0]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF', fontSize: '13px' }}>
                      {asset.symbol || asset.short_name}
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ color: '#B7BDC6' }}>
                    <StarBorderIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 0.5, fontSize: '15px' }}>
                  {formatPrice(asset.priceUsd || Math.random() * 1000)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={assetType === 'crypto' 
                      ? `${calculate24hrChange(asset.symbol) >= 0 ? '+' : ''}${calculate24hrChange(asset.symbol).toFixed(2)}%`
                      : asset.change_percentage || '+0.00%'
                    }
                    size="small"
                    sx={{
                      background: (assetType === 'crypto' 
                        ? calculate24hrChange(asset.symbol) >= 0
                        : parseFloat(asset.change_percentage || 0) >= 0
                      ) ? 'rgba(2, 192, 118, 0.2)' : 'rgba(248, 73, 96, 0.2)',
                      color: (assetType === 'crypto' 
                        ? calculate24hrChange(asset.symbol) >= 0
                        : parseFloat(asset.change_percentage || 0) >= 0
                      ) ? '#02C076' : '#F84960',
                      fontSize: '10px',
                      fontWeight: 600,
                      height: '18px'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                    {assetType === 'crypto' 
                      ? `Vol: ${formatNumber(asset.volumeUsd24Hr || 0)}` 
                      : 'Market'}
                  </Typography>
                </Box>
              </CardContent>
            </AssetTickerCard>
          ))}
        </Stack>
      </Box>

      {/* Stock Holdings & Crypto Assets Section - COMMENTED OUT */}
      {/* 
      <Grid container spacing={3}>
        
        {/* Assets & Market Performance */}
        {/* 
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Assets */}
            {/* 
            <Grid item xs={12}>
              <TradingCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '18px' }}>
                      {assetType === 'crypto' ? 'Crypto Assets' : 'Stock Holdings'}
                    </Typography>
                    <Box sx={{ display: 'flex', background: '#1E2328', borderRadius: '8px', padding: '3px' }}>
                      <Button
                        size="small"
                        onClick={() => setAssetType('stocks')}
                        sx={{
                          minWidth: '70px',
                          fontSize: '12px',
                          padding: '6px 12px',
                          background: assetType === 'stocks' ? '#F7931A' : 'transparent',
                          color: assetType === 'stocks' ? '#000' : '#B7BDC6',
                          borderRadius: '6px',
                          fontWeight: assetType === 'stocks' ? 700 : 500,
                          '&:hover': {
                            background: assetType === 'stocks' ? '#F7931A' : 'rgba(247, 147, 26, 0.1)'
                          }
                        }}
                      >
                        Stocks
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setAssetType('crypto')}
                        sx={{
                          minWidth: '70px',
                          fontSize: '12px',
                          padding: '6px 12px',
                          background: assetType === 'crypto' ? '#F7931A' : 'transparent',
                          color: assetType === 'crypto' ? '#000' : '#B7BDC6',
                          borderRadius: '6px',
                          fontWeight: assetType === 'crypto' ? 700 : 500,
                          '&:hover': {
                            background: assetType === 'crypto' ? '#F7931A' : 'rgba(247, 147, 26, 0.1)'
                          }
                        }}
                      >
                        Crypto
                      </Button>
                    </Box>
                  </Box>
                  <List sx={{ padding: 0 }}>
                    {portfolioAssets.map((asset, index) => (
                      <ListItem 
                        key={index} 
                        sx={{ 
                          padding: '12px 0', 
                          borderBottom: index < portfolioAssets.length - 1 ? '1px solid #2B3139' : 'none',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          mb: 1,
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            background: 'rgba(247, 147, 26, 0.08)',
                            transform: 'translateX(4px)'
                          }
                        }}
                        onClick={() => handleAssetClick(assetType === 'crypto' 
                          ? { symbol: asset.symbol } 
                          : { short_name: asset.symbol }, assetType)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar 
                                  src={asset.logo}
                                  sx={{ width: 32, height: 32, background: '#F7931A', fontSize: '12px' }}
                                >
                                  {asset.symbol[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '16px' }}>
                                    {asset.symbol}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '12px' }}>
                                    {asset.name}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '16px' }}>
                                  {asset.priceUsd ? formatPrice(asset.priceUsd) : '$0.00'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: asset.change.startsWith('+') ? '#02C076' : asset.change.startsWith('-') ? '#F84960' : '#B7BDC6', fontSize: '12px', fontWeight: 600 }}>
                                  {asset.change}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '12px', display: 'block' }}>
                                  {assetType === 'crypto' ? `${asset.amount} (${asset.usdValue})` : asset.amount}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </TradingCard>
            </Grid>


          </Grid>
        </Grid>

      </Grid>
      */}

      {/* Hidden components for backward compatibility */}
      <Box sx={{ display: 'none' }}>
        <CoinSlide />
        <BalanceCard />
        <StockSlide />
      </Box>

    </TradingDashboard>
  )
}

export default Index